import axios, { AxiosResponse } from 'axios';
import {
  SplitPayTransactionListResponse,
  SplitPayTransactionDetailsResponse,
  DeleteSplitPayTransactionResponse,
  ClearSplitPayTransactionsResponse,
  ProcessedSplitPayTransactionRequest,
  ProcessedSplitPayTransactionResponse,
  TimedPullListResponse,
  TimedPullDetails,
  TimedPullRequest,
  TimedPullResponse,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const splitPayTransactionApi = {
  // Create a new splitpay transaction
  createSplitPayTransaction: async (
    payerId: string,
    request: ProcessedSplitPayTransactionRequest,
    billerId: string,
    authToken: string
  ): Promise<ProcessedSplitPayTransactionResponse> => {
    const authHeader = `Basic ${btoa(`${billerId}:${authToken}`)}`;
    const response: AxiosResponse<ProcessedSplitPayTransactionResponse> = await apiClient.put(
      `/protectpay/Payers/${payerId}/PaymentMethods/ProcessedTransactions/`,
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },

  // Get all splitpay transactions (Admin endpoint)
  getAllSplitPayTransactions: async (
    limit?: number,
    offset?: number,
    payerId?: string,
    paymentMethodId?: string,
    status?: 'SUCCESS' | 'FAILURE' | 'PENDING',
    startDate?: string,
    endDate?: string,
    minAmount?: number,
    maxAmount?: number,
    isReversed?: boolean,
    format?: 'json' | 'csv'
  ): Promise<SplitPayTransactionListResponse | string> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (payerId) params.append('payerId', payerId);
    if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (minAmount !== undefined) params.append('minAmount', minAmount.toString());
    if (maxAmount !== undefined) params.append('maxAmount', maxAmount.toString());
    if (isReversed !== undefined) params.append('isReversed', isReversed.toString());
    if (format) params.append('format', format);

    const queryString = params.toString();
    const url = `/admin/protectpay/splitpay-transactions${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);

    // If format is CSV, return the raw text response
    if (format === 'csv') {
      return response.data as string;
    }

    return response.data as SplitPayTransactionListResponse;
  },

  // Get specific splitpay transaction details (Admin endpoint)
  getSplitPayTransactionDetails: async (transactionId: string): Promise<SplitPayTransactionDetailsResponse> => {
    const response: AxiosResponse<SplitPayTransactionDetailsResponse> = await apiClient.get(
      `/admin/protectpay/splitpay-transactions/${transactionId}`
    );
    return response.data;
  },

  // Delete a specific splitpay transaction (Admin endpoint)
  deleteSplitPayTransaction: async (transactionId: string): Promise<DeleteSplitPayTransactionResponse> => {
    const response: AxiosResponse<DeleteSplitPayTransactionResponse> = await apiClient.delete(
      `/admin/protectpay/splitpay-transactions/${transactionId}`
    );
    return response.data;
  },

  // Clear all splitpay transactions (Admin endpoint)
  clearAllSplitPayTransactions: async (): Promise<ClearSplitPayTransactionsResponse> => {
    const response: AxiosResponse<ClearSplitPayTransactionsResponse> = await apiClient.delete(
      '/admin/protectpay/splitpay-transactions'
    );
    return response.data;
  },

  // Download splitpay transactions as CSV (Admin endpoint)
  downloadSplitPayTransactionsCSV: async (
    limit?: number,
    offset?: number,
    payerId?: string,
    paymentMethodId?: string,
    status?: 'SUCCESS' | 'FAILURE' | 'PENDING',
    startDate?: string,
    endDate?: string,
    minAmount?: number,
    maxAmount?: number,
    isReversed?: boolean
  ): Promise<string> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (payerId) params.append('payerId', payerId);
    if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (minAmount !== undefined) params.append('minAmount', minAmount.toString());
    if (maxAmount !== undefined) params.append('maxAmount', maxAmount.toString());
    if (isReversed !== undefined) params.append('isReversed', isReversed.toString());
    params.append('format', 'csv');

    const queryString = params.toString();
    const url = `/admin/protectpay/splitpay-transactions${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'text',
    });

    return response.data;
  },

  adminListTimedPulls: async (
    limit?: number,
    offset?: number,
    sourceAccountNum?: number,
    recipientAccountNum?: number,
    status?: 'PENDING' | 'COMPLETED' | 'FAILED',
    startDate?: string,
    endDate?: string
  ): Promise<TimedPullListResponse> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (sourceAccountNum !== undefined) params.append('sourceAccountNum', sourceAccountNum.toString());
    if (recipientAccountNum !== undefined) params.append('recipientAccountNum', recipientAccountNum.toString());
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `/admin/protectpay/splitpay-transactions/timed-pulls${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse<TimedPullListResponse> = await apiClient.get(url);
    return response.data;
  },

  adminGetTimedPullDetails: async (timedPullId: string): Promise<TimedPullDetails> => {
    const response: AxiosResponse<TimedPullDetails> = await apiClient.get(
      `/admin/protectpay/splitpay-transactions/timed-pulls/${timedPullId}`
    );
    return response.data;
  },

  createTimedPull: async (
    request: TimedPullRequest,
    certStr: string,
    termId: string
  ): Promise<TimedPullResponse> => {
    const authHeader = `Basic ${btoa(`${certStr}:${termId}`)}`;
    const response: AxiosResponse<TimedPullResponse> = await apiClient.put(
      `/propayAPI/TimedPull/`,
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },
};
